import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileDown, Wrench } from "lucide-react";
import api from "@/lib/api";
import { ViewOnlineButton } from "@/components/bookings/IssuedConfirmationView";
import { openDocument } from "@/lib/documents";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { Breadcrumbs, SectionCard } from "@/components/shared/PageChrome";
import { StackedList } from "@/components/shared/StackedList";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { CommercialTermsPanel, ConfirmationValidity } from "@/components/bookings/CommercialTermsPanel";
import { AgreementPanel } from "@/components/bookings/ExtensionAgreement";
import { TermsByPeriod } from "@/components/bookings/TermsByPeriod";
import { RevisionLabel } from "@/components/bookings/CommercialTermsPanel";

export default function ClientBookingDetail() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const emptyForm = { change_type: "EXTENSION", reason: "", stay_id: "", check_out_date: "", room_count: "" };
  const [form, setForm] = useState(emptyForm);
  const navigate = useNavigate();

  const load = () => api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dated = form.change_type !== "CANCELLATION";
  const activeStays = (booking?.stays || []).filter((s) => s.status !== "CANCELLED");

  const submit = async () => {
    try {
      const requested_payload = dated ? {
        stay_id: form.stay_id || (activeStays.length === 1 ? activeStays[0].id : undefined),
        check_out_date: form.check_out_date || undefined,
        room_count: form.room_count ? Number(form.room_count) : undefined,
      } : undefined;
      await api.post(`/bookings/${id}/changes`, { change_type: form.change_type, reason: form.reason, requested_payload });
      toast.success("Change request sent to Jigsaw for review");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit request"); }
  };

  if (error) return <ErrorState testId="client-booking-detail-error" />;
  if (!booking) return <LoadingState testId="client-booking-detail-loading" />;

  return (
    <div data-testid="client-booking-detail-page" className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Breadcrumbs items={[{ label: "Bookings", to: "/client/bookings" }, { label: booking.booking_reference }]} />
          <button onClick={() => navigate("/client/bookings")} data-testid="client-booking-back-button" className="sr-only">Back to Bookings</button>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono-ref rounded-md bg-jigsaw-navy px-2 py-0.5 text-[12px] font-semibold text-white">{booking.booking_reference}</span>
            <StatusBadge status={booking.status} />
          </div>
          <h1 className="mt-2 text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">{booking.stays?.[0]?.property_name ? `${booking.booking_reference} · ${booking.stays[0].property_name}` : booking.booking_reference}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
        {booking.status !== "CANCELLED" && <Button data-testid="report-repair-button" variant="outline" onClick={() => navigate(`/client/support?intent=repair&booking=${id}`)}><Wrench className="mr-1.5 h-4 w-4" /> Report a repair</Button>}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="request-change-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark">Request extension / cancellation</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request a change</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={form.change_type} onValueChange={(v) => setForm({ ...form, change_type: v })}>
                  <SelectTrigger data-testid="change-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="EXTENSION">Extension</SelectItem><SelectItem value="AMENDMENT">Amendment</SelectItem><SelectItem value="CANCELLATION">Cancellation</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Reason</Label><Textarea data-testid="change-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Tell Jigsaw what you need" /></div>
              {dated && (
                <>
                  {activeStays.length > 1 && (
                    <div>
                      <Label>Which stay?</Label>
                      <Select value={form.stay_id} onValueChange={(v) => setForm({ ...form, stay_id: v })}>
                        <SelectTrigger data-testid="change-stay-select"><SelectValue placeholder="Choose the stay" /></SelectTrigger>
                        <SelectContent>{activeStays.map((s) => <SelectItem key={s.id} value={s.id}>{s.property_name || "Property TBC"} · {s.check_in_date} → {s.check_out_date}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Preferred new check-out</Label><Input data-testid="change-check-out-input" type="date" value={form.check_out_date} onChange={(e) => setForm({ ...form, check_out_date: e.target.value })} /></div>
                    <div><Label>Rooms needed</Label><Input data-testid="change-rooms-input" type="number" min="1" step="1" value={form.room_count} onChange={(e) => setForm({ ...form, room_count: e.target.value })} /></div>
                  </div>
                </>
              )}
              <p className="text-xs text-jigsaw-muted">This request is routed to Jigsaw staff for review - nothing is applied automatically.</p>
              {dated && booking.commercial_terms && <p data-testid="change-terms-note" className="rounded-md border border-jigsaw-border bg-jigsaw-bg px-3 py-2 text-xs text-jigsaw-navy">Your agreed terms cover {booking.commercial_terms.covered_check_in} → {booking.commercial_terms.covered_check_out}. Terms for any additional nights are agreed separately with Jigsaw - they are not assumed to continue.</p>}
            </div>
            <DialogFooter><Button data-testid="change-save-button" onClick={submit} disabled={!form.reason}>Send request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-5">
          <SectionCard title="Stays" testId="client-stays-panel" bodyClassName="px-0 pb-0">
            <div className="px-5 pb-4 sm:hidden">
              <StackedList testId="client-stays-stacked" items={booking.stays} title={(st) => st.property_name || "Property TBC"} status={(st) => <StatusBadge status={st.status} />}
                fields={[{ label: "Check-in", value: (st) => st.check_in_date, mono: true }, { label: "Check-out", value: (st) => st.check_out_date, mono: true }, { label: "Rooms", value: (st) => st.room_count }]} />
            </div>
            <div className="hidden sm:block">
              <Table bare data-testid="client-stays-table">
                <TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Check-in</TableHead><TableHead>Check-out</TableHead><TableHead className="text-right">Rooms</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {booking.stays.map((st) => (
                    <TableRow key={st.id} data-testid={`client-stay-row-${st.id}`}>
                      <TableCell className="font-medium">{st.property_name || "Property TBC"}</TableCell>
                      <TableCell className="font-mono-ref">{st.check_in_date}</TableCell>
                      <TableCell className="font-mono-ref">{st.check_out_date}</TableCell>
                      <TableCell className="text-right font-mono-ref">{st.room_count}</TableCell>
                      <TableCell><StatusBadge status={st.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
          <CommercialTermsPanel terms={booking.commercial_terms} testId="client-commercial-terms" audience="client" />
          <TermsByPeriod data={booking.terms_by_period} testId="client-terms-by-period" audience="client" />
        </div>

        <aside className="space-y-4 xl:self-start" aria-label="Confirmation documents">
          <SectionCard title="Confirmation" testId="client-confirmation-panel" bodyClassName="px-5 pb-4">
            {booking.confirmation?.validity && <div className="mb-3"><ConfirmationValidity validity={booking.confirmation.validity} testId="client-confirmation-validity" /></div>}
            {!booking.confirmation || booking.confirmation.revisions.length === 0 ? (
              <p className="text-sm text-jigsaw-muted">Your confirmation document will appear here once issued.</p>
            ) : (
              <ul className="space-y-2">
                {booking.confirmation.revisions.map((r, i) => (
                  <li key={r.id} className="flex flex-col gap-1.5 rounded-md border border-jigsaw-border px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-jigsaw-navy">Revision {r.revision_number}</span>
                    <span className="flex gap-2"><ViewOnlineButton bookingId={id} revision={r} testId={`client-view-online-${r.revision_number}`} />
                    <Button data-testid={`download-confirmation-${r.id}`} size="sm" variant="outline" onClick={() => openDocument(r.document_id)}><FileDown className="mr-1 h-3.5 w-3.5" /> Download</Button></span></div>
                    <RevisionLabel revision={r} testId={`client-confirmation-revision-${r.revision_number}-label`} short />
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </aside>
      </div>

      <Card className="border-jigsaw-border">
        <CardHeader><CardTitle className="text-base text-jigsaw-navy">Change requests</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {booking.changes.length === 0 && <p className="text-sm text-jigsaw-muted">No change requests yet.</p>}
          {booking.changes.map((c) => (
            <div key={c.id} data-testid={`client-change-row-${c.id}`} className="rounded-md border border-jigsaw-border p-3 text-sm">
             <div className="flex items-center justify-between">
              <div>
                <span className="text-jigsaw-navy">{c.change_type} - {c.reason}</span>
                {c.applied?.after && <p className="text-xs text-jigsaw-success">Applied: check-out {c.applied.after.check_out_date} · {c.applied.after.room_count} room(s)</p>}
                {c.decision_notes && <p className="text-xs text-jigsaw-muted">Jigsaw: {c.decision_notes}</p>}
              </div>
              <StatusBadge status={c.status} />
            </div>
             <AgreementPanel change={c} staff={false} onChanged={load} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
